import assert from "assert";
import { getProxyMock } from "common/data/proxyMock";
import { assertIsNotUndefined } from "common/utils/assertOfType";
import { OS, getOS, gpuUnavailable } from "common/utils/context";
import { Color } from "three";
import { defaultCameraSettings } from "./CameraSettings";
import { EnvPreset } from "./EnvPreset";
import type { HTTPMaterialFactory } from "./HTTPMaterialFactory";
import { envPresetSceneLights } from "./sceneLights";
import type { SharedWebGL } from "./SharedWebGL";
import type { RendererParamOverrides } from "./threejs/higharc/SSFXRendererTypes";
import { THREEViewerBase } from "./THREEViewerBase";
import type { THREEViewerOptions } from "./THREEViewerOptions";
import { THREEViewerRenderer } from "./THREEViewerRenderer";

// Re-export for backwards compatibility
export type { THREEViewerOptions } from "./THREEViewerOptions";

const defaultOptions: THREEViewerOptions = Object.freeze({
  showAxes: true,
  showShadowCamera: false,
  shadows: true,
  ssfxRenderer: false,
  sceneLights: envPresetSceneLights[EnvPreset.MORNING],
  gui: false,
  materialFactoryGUI: true,
  debugFeaturesEnabled: false,
  animate: false,
  cameraSettings: defaultCameraSettings,
  forceEnableIfGpuUnavailable: false,
  throwOnGLError: false,
  logGLResourceUsage: false,
  useMSAA: false,
});

/**
 * THREEViewer is a wrapper around a three.js scene and incorporates {@link SSFXRenderer}.
 * This is the main class for rendering 3D buildings. It is heavyweight and it is not recommended
 * to create more than one per page. {@link THREEViewerSystem} combines a THREEViewer with asset loading.
 * There are also various higher level React components that use THREEViewer to render their content.
 */
export default class THREEViewer extends THREEViewerBase {
  get hasSSFXRenderer(): boolean {
    return this.options.ssfxRenderer;
  }

  get guiEnabled(): boolean {
    return this.guiEnabledInternal;
  }
  private guiEnabledInternal: boolean = false;
  private debugFeaturesEnabledInternal: boolean = false;

  private readonly options = defaultOptions;

  private viewerRendererInternal: THREEViewerRenderer | undefined;

  override get viewerRenderer(): THREEViewerRenderer {
    assertIsNotUndefined(this.viewerRendererInternal);
    return this.viewerRendererInternal;
  }

  readonly performanceTierPromise: Promise<void> | undefined;

  guiPromise: Promise<void> | undefined;

  constructor({
    sharedWebGL,
    container,
    options: userOptions = {},
  }: {
    sharedWebGL?: SharedWebGL | null;
    container?: HTMLElement | null;
    options: Partial<THREEViewerOptions>;
  }) {
    const options = { ...defaultOptions, ...userOptions };

    if (gpuUnavailable() && !options.forceEnableIfGpuUnavailable) {
      return getProxyMock() as THREEViewer;
    }

    super({
      name: "Main THREEViewer",
      options,
      container,
      useSsfxRendererScene: true,
      pixelRatio: window.devicePixelRatio,
    });

    assert(this.scene.ssfxRendererScene);

    this.guiEnabledInternal = options.gui;
    this.debugFeaturesEnabledInternal = options.debugFeaturesEnabled;
    this.options = options;
    this.sharedWebGL = sharedWebGL ?? null;

    try {
      this.initViewerRenderer();
    } catch (e) {
      this.removeResizeListener();
      throw e;
    }

    this.scene.scene.name = "Building";
    this.scene.scene.background = new Color(0xffffff);

    this.setCameraSettings(this.options.cameraSettings, this.getAspectRatio());
    this.initializeControls(this.options.cameraSettings);

    if (this.options.ssfxRenderer) {
      const PERFORMANCE_TIER_LOADING_KEY = "performanceTierLoading";
      this.viewerRenderer.suspend(PERFORMANCE_TIER_LOADING_KEY);
      this.performanceTierPromise = (async () => {
        const performanceTier = await this.viewerRenderer.services.getPerformanceTierByScore();
        this.viewerRenderer.setPerformanceTier(performanceTier);

        let appliedPixelRatio = window.devicePixelRatio;

        const gpuInfo = await this.viewerRenderer.services.gpuInfo;
        if (getOS() === OS.WINDOWS || getOS() === OS.IPAD) {
          // Apply resolution limits depending on GPU performance so that we perform better on low-powered integrated GPUs.
          // Estimating performance is less reliable on Macs, and reasonably powerful M series processors are
          // often combined with high resolution displays, so we don't apply the same kind of limits.
          if (gpuInfo.fps === undefined || gpuInfo.fps < 140) {
            appliedPixelRatio = Math.min(1, appliedPixelRatio);
          } else if (gpuInfo.fps < 300) {
            if (appliedPixelRatio > 3) {
              appliedPixelRatio = 2;
            }
          }
        } else if (performanceTier.iPhoneOrAndroid) {
          if (appliedPixelRatio > 3) {
            appliedPixelRatio = 2;
          }
        }

        // Limit overall resolution to 4K
        // Total max resolution = screen.width * appliedPixelRatio
        appliedPixelRatio = Math.max(Math.min(Math.floor(4097 / screen.width), appliedPixelRatio), 1);

        this.setPixelRatio(appliedPixelRatio);
        this.viewerRenderer.resume(PERFORMANCE_TIER_LOADING_KEY); // Allow rendering only after the performance tier has been set.
      })();
    }

    void this.scene.ssfxRendererScene.sceneLightsManager.renderSceneLights(options.sceneLights, {});

    if (options.shadows) {
      this.scene.ssfxRendererScene.sceneLightsManager.setupDirectionalLightShadow(options.showShadowCamera);
    }

    this.guiPromise = (async () => {
      await this.viewerRenderer.guiPromise;
    })();
  }

  protected override initViewerRenderer() {
    this.removeContextLostListener();
    this.viewerRendererInternal?.dispose();
    const viewerRenderer = new THREEViewerRenderer({
      options: this.options,
      container: this.container,
      viewer: this,
      sharedWebGL: this.sharedWebGL,
      pixelRatio: this.pixelRatio,
    });
    viewerRenderer.guiEnabled = this.guiEnabledInternal;
    viewerRenderer.setDebugFeaturesEnabled(this.debugFeaturesEnabledInternal);

    if (!this.sharedWebGL) {
      this.setContextLostListener(viewerRenderer.canvas, this.initViewerRenderer.bind(this));
    }

    this.viewerRendererInternal = viewerRenderer;

    this.requestResize();
  }

  setDebugFeaturesEnabled(enabled: boolean) {
    this.debugFeaturesEnabledInternal = enabled;
    if (this.viewerRenderer) {
      this.viewerRenderer.setDebugFeaturesEnabled(enabled);
    }
  }

  toggleGui() {
    this.guiEnabledInternal = !this.guiEnabledInternal;
    if (this.viewerRenderer) {
      this.viewerRenderer.guiEnabled = this.guiEnabledInternal;
    }
    return this.guiEnabledInternal;
  }

  updateSceneRenderParams(params: Omit<RendererParamOverrides, "id" | "source">) {
    this.scene.ssfxRendererScene?.updateRenderParams(params);
  }

  getMaterialFactory(): HTTPMaterialFactory {
    return this.viewerRenderer.services.httpMaterialFactory;
  }

  override set skyboxVisible(visible: boolean) {
    if (this.scene.ssfxRendererScene) {
      this.scene.ssfxRendererScene.sceneLightsManager.skybox.visible = visible;
    }
  }

  suspend(suspender: string): void {
    this.viewerRenderer.suspend(suspender);
  }

  resume(suspender: string): void {
    this.viewerRenderer.resume(suspender);
  }
}
